import React, {useCallback, useEffect, useState} from 'react';
import {Controller, useForm} from "react-hook-form";
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    IconButton,
    Grid,
    Input,
    InputLabel,
    InputAdornment,
    Typography,
    useToastProvider
} from '@deere/fuel-react';
import {DataGrid} from '@mui/x-data-grid';
import {
    CREATE_CRITERIA_CUSTOM_LIKERT_STATE,
    CREATE_CRITERIA_FORM_STATE,
    CREATE_CRITERIA_QUANTITATIVE_STATE,
    LIKERT_SCALE_LIST,
    RESP_TYPE_OPTIONS
} from "../shared/Constants";
import {CustomizedRatings} from "../shared/LikertScale";
import {useHistory, useParams} from 'react-router-dom';
import {useOktaAuth} from '@okta/okta-react';
import {Loading} from "../shared/Loading";
import {FormRadio} from "../shared/FormRadio";
import {FormSelect} from "../shared/FormSelect";
import {ModalDialog} from "../shared/ModalDialog";
import {anyMatchInArray, isNotEmptyArray, getSelectedSubFunctionalAreas} from "../shared/utils";
import {useAuthConfig} from "../shared/helper";
import AutoCompleteCheckbox from "../shared/AutoCompleteCheckbox";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import {useStyles} from "../shared/styles/Crietria";
import {NotFoundPage} from "../shared/NotFoundPage";

export const CriteriaCreateEdit = ({name}) => {
    const classes = useStyles();
    let history = useHistory();
    let {criteriaId} = useParams();
    let authAxios = useAuthConfig();
    const {authState} = useOktaAuth();
    const {enqueueToast} = useToastProvider();
    const {control, handleSubmit, setValue, reset, formState: {errors}} = useForm();
    const [likertScaleList] = useState(LIKERT_SCALE_LIST);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [functionalAreas, setFunctionalAreas] = useState([]);
    const [subFunctionalAreas, setSubFunctionalAreas] = useState([]);
    const [stakeholders, setStakeholders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFunctionalAreaError, setIsFunctionalAreaError] = useState(false);
    const [functionalAreaErrorDesc, setFunctionalAreaErrorDesc] = useState("");
    const [isSubFuncAreaError, setIsSubFuncAreaError] = useState(false);
    const [subFuncAreaErrorDesc, setSubFuncAreaErrorDesc] = useState("");
    const [descCharacterCount, setDescCharacterCount] = useState(0);
    const [calcCharacterCount, setCalcCharacterCount] = useState(0);
    const [rangeCount, setRangeCount] = useState(2);
    const [ranges, setRanges] = useState(CREATE_CRITERIA_QUANTITATIVE_STATE);
    const [rangeError, setRangeError] = useState([]);
    const [is404Error, setIs404Error] = useState(false);
    const [custLikertRangeCount, setCustLikertRangeCount] = useState(2);
    const [custLikertRanges, setCustLikertRanges] = useState(CREATE_CRITERIA_CUSTOM_LIKERT_STATE);
    const [custLikertRangeError, setCustLikertRangeError] = useState([]);
    const [maxStars, setMaxStars] = useState(2);
    const onError = (errors, e) => console.log(errors, e);

    const initState = CREATE_CRITERIA_FORM_STATE;
    const respTypeOptions = RESP_TYPE_OPTIONS;

    const [state, setState] = useState(initState);
    const [isShow, setIsShow] = useState(false);

    const resetState = () => {
        setRangeCount(2);
        setRanges(CREATE_CRITERIA_QUANTITATIVE_STATE)
        setCustLikertRangeCount(2);
        setCustLikertRanges(CREATE_CRITERIA_CUSTOM_LIKERT_STATE)
        setMaxStars(2);
        setState(initState);
        setIsSubFuncAreaError(false)
        setIsFunctionalAreaError(false)
    }

    const filterCategories = (arr, inputValue) => {
        return arr.filter(el => Number(el.id) === Number(inputValue))
    }

    const onStatusUpdate = async () => {
        const data = {status: "INACTIVE"}
        await authAxios.put(`/criteria/update-status/${criteriaId}`, data)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: "Criteria deactivated",
                    isCloseable: true
                });
                setTimeout(() => {
                    history.replace(`/criteria-catalog/view-criteria/${criteriaId}`);
                }, 2000);
            }).catch((error) => {
                enqueueToast({
                    variant: "error",
                    title: "System Error",
                    message: error.response ? error.response.data[error.response.status] : "",
                    isPersistent: true
                });
            });
    }

    const validateFormData = (data) => {
        const subFunctionalIdsList = data.subFunctionalAreas.map(ele => ele.functionalAreaId)
        const functionalIdsList = data.functionalAreas.map(ele => ele.id)

        const isFunctionalAreasValid = anyMatchInArray(subFunctionalIdsList, functionalIdsList)
        const isSubFuncAreasValid = anyMatchInArray(functionalIdsList, subFunctionalIdsList)
        const isNotEmptySubFuncArea = isNotEmptyArray(state.subFunctionalAreas)
        const isNotEmptyFuncArea = isNotEmptyArray(state.functionalAreas)

        if (!isNotEmptyFuncArea || !isFunctionalAreasValid) {
            setIsFunctionalAreaError(true)
            setFunctionalAreaErrorDesc(!isFunctionalAreasValid ? "Choose all related functional areas" : "Please select a functional area")
        } else {
            setIsFunctionalAreaError(false)
            setFunctionalAreaErrorDesc("")
        }

        if (!isNotEmptySubFuncArea || !isSubFuncAreasValid) {
            setIsSubFuncAreaError(true)
            setSubFuncAreaErrorDesc(!isSubFuncAreasValid ? "Choose any one of related Sub functional areas" : "Please select a sub functional area")
        } else {
            setIsSubFuncAreaError(false)
            setSubFuncAreaErrorDesc("")
        }

        return isNotEmptyFuncArea && isNotEmptySubFuncArea && isFunctionalAreasValid && isSubFuncAreasValid;
    }

    const validateRanges = (ranges) => {
        const isRangeEmpty = ranges.every(item => item.rangeMin !== "" || item.rangeMax !== "")
        const isValueEmpty = ranges.every(item => item.valueMin !== "" ||  item.valueMax !== "")
        const isLabelEmpty = ranges.every(item => item.label !== "")

        if (!isRangeEmpty || !isValueEmpty || !isLabelEmpty) {
            setRangeError(prevError => [...prevError, "Enter valid values for Range, Value, Label"])
        }
        return isRangeEmpty && isValueEmpty && isLabelEmpty;
    }

    const validateCustLikertRanges = (custLikertranges) => {
        const isPointsRangeEmpty = custLikertranges.every(item => item.pointsRange !== "")
        const isCustLikertLabelEmpty = custLikertranges.every(item => item.label !== "")

        if (!isPointsRangeEmpty || !isCustLikertLabelEmpty) {
            setCustLikertRangeError(prevError => [...prevError, "Enter valid values for Range, Label"])
        }
        return isPointsRangeEmpty && isCustLikertLabelEmpty;
    }

    const onUpdate = async () => {
        const isValid = validateFormData(state)
        const isValidRanges = isShow ? validateRanges(ranges) : true;
        const isValidLikertRanges = validateCustLikertRanges(custLikertRanges);
        const putData = {
            ...state, ranges: isShow ? ranges : [], custLikertRanges
        }
        isValid && isValidRanges && isValidLikertRanges && await authAxios.put(`/criteria/${criteriaId}`, putData)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: "Criteria updated",
                    isCloseable: true
                });
            }).catch((error) => {
                enqueueToast({
                    variant: "error",
                    title: "Error occurred while Saving Criteria Data",
                    message: error.response ? error.response.data[error.response.status] : "",
                    isPersistent: true
                });
                console.log(error);
            });
    }

    const onSubmit = async (data) => {
        const isValid = validateFormData(data);
        const isValidRanges = isShow ? validateRanges(ranges) : true;
        const isValidLikertRanges = validateCustLikertRanges(custLikertRanges);
        const postData = {
            ...data, ranges: isShow ? ranges : [], custLikertRanges
        }
        isValid && isValidRanges && isValidLikertRanges && await authAxios.post(`/criteria`, postData)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: "Criteria created",
                    isCloseable: true
                });
                reset();
                resetState();
            }).catch((error) => {
                enqueueToast({
                    variant: "error",
                    title: "System Error",
                    message: error.response ? error.response.data[error.response.status] : "",
                    isPersistent: true
                });
                console.log(error);
            });
    }

    const onCancel = () => {
        resetState();
        history.push("/criteria-catalog/");
    }

    const onRespTypeChange = event => {
        setState({
            ...state,
            responseType: event.target.value,
            ranges: [],
            custLikertRanges: []
        });
        if (event.target.value !== 3) {
            setRanges([])
        }
        if (event.target.value !== 4) {
            setCustLikertRanges([])
        }
        setIsShow(false)
    }

    const onRatingsChange = event => {
        const {id, value} = event.target;
        const index = parseInt(id)

        let newRatingsArr = [...state.ratings];
        state.ratings.map((data) => {
            newRatingsArr[index].description = value;
            return newRatingsArr;
        });
        setState({
            ...state,
            ratings: newRatingsArr
        });
    }

    const onCriteriaChange = event => {
        setState({
            ...state,
            criteria: event.target.value
        });
    }

    const onDescChange = event => {
        setState({
            ...state,
            description: event.target.value
        });
    }

    const onCalcDescChange = event => {
        setState({
            ...state,
            calculation: event.target.value
        });
    }

    const onCategorySelect = event => {
        setSubCategories([]);
        setValue("subCategory", "");
        setState({
            ...state,
            category: event.target.value
        });
        let item = filterCategories(categories, event.target.value)
        setSubCategories(item[0].subCategories);
    }

    const onSubCategorySelect = event => {
        setState({
            ...state,
            subCategory: event.target.value
        });
    }

    const handleFunctionalArea = (value) => {
        if (value === null || value.length === 0) {
            setState({
                ...state,
                functionalAreas: [],
                subFunctionalAreas: [],
                stakeholders: []
            });
            setStakeholders([]);
            setSubFunctionalAreas([]);

        } else {
            const validFunctionalAreaIds = value.map(area => area.id);
            const filteredSubFunctionalAreas = state.subFunctionalAreas.filter(subFunctionalArea =>
                validFunctionalAreaIds.includes(subFunctionalArea.functionalAreaId)
            );
            const validFunctionAreaDescriptions = value.map(area => area.description);
            const filteredStakeholderTypes = state.stakeholders.filter(stakeholder => {
                const description = stakeholder.description;
                if (description) {
                    const [firstPart] = description.split("-").map(part => part.trim());
                    return firstPart === "All" || validFunctionAreaDescriptions.includes(firstPart);
                }
                return false;
            })

            setState({
                ...state,
                functionalAreas: value,
                subFunctionalAreas: filteredSubFunctionalAreas,
                stakeholders: filteredStakeholderTypes
            });
            getStakeholders(value).then(res => {
                setStakeholders(res.data);
            });
            const tempSubFunctionalAreas = value.map(item => item.subFunctionalArea && item.subFunctionalArea)
            setSubFunctionalAreas([...new Set(tempSubFunctionalAreas.flat())]);
        }
    }

    const handleSubFunctionalArea = (value) => {
        setState({
            ...state,
            subFunctionalAreas: value
        });
    }

    const handleStakeholder = value => {
        setState({
            ...state,
            stakeholders: value
        });
    }

    const handleRowDelete = useCallback(
        (id) => {
            const filteredRows = ranges.filter((row) => {
                return row.id !== id;
            });
            const updatedRows = filteredRows.map((item, index) => {
                item.order = index + 1;
                return item;
            })
            setRanges(updatedRows)
        },
        [ranges]
    );

    const handleRangeShow = () => {
        setIsShow(true);
        setRanges(CREATE_CRITERIA_QUANTITATIVE_STATE)
        setRangeCount(2)
    }

    const handleRangeHide = () => {
        setIsShow(false);
        setRanges(CREATE_CRITERIA_QUANTITATIVE_STATE)
    }

    const handleRangeAdd = () => {
        let obj = {
            rangeMin: "",
            rangeMax: "",
            valueMin: "",
            valueMax: "",
            label: "",
            status: "A"
        }
        setRangeCount(rangeCount + 1)
        const idObj = {id: rangeCount + 1}
        obj = {...idObj, ...obj};
        ranges.splice(ranges.length - 1, 0, obj)
        ranges.map((item, index) => {
            item.order = index + 1;
            return item;
        });
        setRanges(ranges)
    }

    const handleRangeMinChange = (id, value) => {
        setRangeError([]);
        const updateData = ranges.map(item => {
            if (item.id === id) {
                item.rangeMin = value
            }
            return item;
        })
        setRanges(updateData)
    }

    const handleRangeMaxChange = (id, value) => {
        setRangeError([]);
        const updateData = ranges.map(item => {
            if (item.id === id) {
                item.rangeMax = value
            }
            return item;
        })
        setRanges(updateData)
    }

    const handleValueMinChange = (id, value) => {
        setRangeError([]);
        const updateData = ranges.map(item => {
            if (item.id === id) {
                item.valueMin = parseFloat(value)
            }
            return item;
        })
        setRanges(updateData)
    }

    const handleValueMaxChange = (id, value) => {
        setRangeError([]);
        const updateData = ranges.map(item => {
            if (item.id === id) {
                item.valueMax = parseFloat(value)
            }
            return item;
        })
        setRanges(updateData)
    }

    const handleLabelChange = (id, value) => {
        setRangeError([]);
        const updateData = ranges.map(item => {
            if (item.id === id) {
                item.label = value
            }
            return item;
        })
        setRanges(updateData)
    }

    const rowsData = ranges.map((item) => {
        return {
            rangeMin: item.rangeMin,
            rangeMax: item.rangeMax,
            valueMin: item.valueMin,
            valueMax: item.valueMax,
            label: item.label,
            id: item.id,
            order: item.order
        };
    });

    const columns = [
        {
            field: 'rangeMin',
            renderHeader: (params) => (
                <Box>
                    <Typography size="lg" variant="label">
                        Points
                    </Typography>
                    <Typography size="md" variant="label" >
                        Range Min
                    </Typography>
                </Box>
            ),
            minWidth: 120,
            maxWidth: 120,
            sortable: false,
            renderCell: (params) => {
                return (
                    <FormControl required>
                        <Controller
                            rules={{required: true}}
                            control={control}
                            defaultValue={params.row.rangeMin}
                            value={params.row.rangeMin}
                            name={`${params.row.id}rangeMin`}
                            render={({
                                         onChange,
                                         value
                                     }) => (
                                <Input
                                    id={`${params.row.id}rangeMin`}
                                    value={value}
                                    onChange={(e) => {
                                        onChange(e.target.value)
                                        handleRangeMinChange(params.row.id, e.target.value);
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">%</InputAdornment>}
                                />
                            )}
                        />
                    </FormControl>
                )
            }
        },
        {
            field: 'rangeMax',
            headerName: 'Range Max',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "25px"}}>
                        Range Max
                    </Typography>
                </Box>
            ),
            minWidth: 120,
            maxWidth: 120,
            sortable: false,
            renderCell: (params) => {
                return (
                    <FormControl required>
                        <Controller
                            rules={{required: true}}
                            control={control}
                            defaultValue={params.row.rangeMax}
                            value={params.row.rangeMax}
                            name={`${params.row.id}rangeMax`}
                            render={({
                                         onChange,
                                         value
                                     }) => (
                                <Input
                                    id={`${params.row.id}rangeMax`}
                                    value={value}
                                    onChange={(e) => {
                                        onChange(e.target.value)
                                        handleRangeMaxChange(params.row.id, e.target.value);
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">%</InputAdornment>}
                                />
                            )}
                        />
                    </FormControl>
                )
            }
        },
        {
            field: '',
            headerName: '',
            sortable: false,
            renderCell: (params) => {
                return (
                    <>
                        <Box style={{
                            borderLeft: "2px solid black",
                            height: "200px",
                            position: "absolute",
                            left: "290px"
                        }}/>
                        <Box style={{borderBottom: "2px solid black", width: "100px"}}/>
                    </>
                )
            }
        },
        {
            field: 'valueMin',
            renderHeader: (params) => (
                <Box>
                    <Typography size="lg" variant="label">
                        Performance
                    </Typography>
                    <Typography size="md" variant="label" >
                        Value Min
                    </Typography>
                </Box>
            ),
            sx: {padding: 0},
            minWidth: 130,
            maxWidth: 130,
            sortable: false,
            renderCell: (params) => {
                return (
                    <FormControl required>
                        <Controller
                            rules={{required: true}}
                            control={control}
                            defaultValue={params.row.valueMin}
                            value={params.row.valueMin}
                            name={`${params.row.id}valueMin`}
                            render={({
                                         onChange,
                                         value
                                     }) => (
                                <Input
                                    id={`${params.row.id}valueMin`}
                                    value={value}
                                    onChange={(e) => {
                                        onChange(e.target.value)
                                        handleValueMinChange(params.row.id, e.target.value);
                                    }}
                                />
                            )}
                        />
                    </FormControl>
                )
            }
        },
        {
            field: 'valueMax',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "25px"}}>
                        Value Max
                    </Typography>
                </Box>
            ),
            sx: {padding: 0},
            minWidth: 120,
            maxWidth: 120,
            sortable: false,
            renderCell: (params) => {
                return (
                    <FormControl required>
                        <Controller
                            rules={{required: true}}
                            control={control}
                            defaultValue={params.row.valueMax}
                            value={params.row.valueMax}
                            name={`${params.row.id}valueMax`}
                            render={({
                                         onChange,
                                         value
                                     }) => (
                                <Input
                                    id={`${params.row.id}valueMax`}
                                    value={value}
                                    onChange={(e) => {
                                        onChange(e.target.value)
                                        handleValueMaxChange(params.row.id, e.target.value);
                                    }}
                                />
                            )}
                        />
                    </FormControl>
                )
            }
        },
        {
            field: 'label',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "25px"}}>
                        Label
                    </Typography>
                </Box>
            ),
            minWidth: 250,
            maxWidth: 250,
            sortable: false,
            renderCell: (params) => {
                return (
                    <FormControl required>
                        <Controller
                            rules={{required: true}}
                            control={control}
                            defaultValue={params.row.label}
                            value={params.row.label}
                            name={`${params.row.id}label`}
                            render={({
                                         onChange,
                                         value
                                     }) => (
                                <Input
                                    placeholder="Write a label"
                                    id={`${params.row.id}label`}
                                    value={value}
                                    onChange={(e) => {
                                        onChange(e.target.value)
                                        handleLabelChange(params.row.id, e.target.value);
                                    }}
                                />
                            )}
                        />
                    </FormControl>
                )
            }
        },
        {
            field: 'id',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "25px"}}>
                        Action
                    </Typography>
                </Box>
            ),
            sortable: false,
            renderCell: (params) => {
                if (params.row.order === 1 || params.row.order === ranges.length) {
                    return (
                        <></>
                    )
                } else {
                    return (
                        <IconButton aria-label="delete" edge="end"
                                    onClick={() => handleRowDelete(params.row.id)}>
                            <DeleteIcon/>
                        </IconButton>
                    )
                }
            }
        }
    ];

    const handleLikertRowDelete = useCallback(
        (id) => {
            const filteredRows = custLikertRanges.filter((row) => {
                return row.id !== id;
            });

            const updatedRows = filteredRows.map((item, index) => {
                return {
                    ...item,
                    order: index + 1
                };
            });

            setCustLikertRanges(updatedRows)
            setCustLikertRangeCount(updatedRows.length)
            setMaxStars(maxStars - 1)
        },
        [custLikertRanges,maxStars]
    );

    const handleLikertRangeAdd = () => {
        let obj = {
            pointsRange: "",
            label: "",
            status: "A"
        }
        if (maxStars < 5){
            setCustLikertRangeCount(custLikertRangeCount + 1)
            const idObj = {id: custLikertRanges.length + 1}
            obj = {...idObj, ...obj};
            const newCustLikertRanges = custLikertRanges;
            newCustLikertRanges.splice(newCustLikertRanges.length - 1, 0, obj)
            const updatedCustLikertRanges = newCustLikertRanges.map((item, index) => {
                item.order = index + 1;
                return item;
            });
            setCustLikertRanges(updatedCustLikertRanges)
            setMaxStars(maxStars + 1)
        }
    }

    const handleLikertPointsRangeChange = (id, value) => {
        setCustLikertRangeError([]);
        const updateData = custLikertRanges.map(item => {
            if (item.id === id) {
                item.pointsRange = value
            }
            return item;
        })
        setCustLikertRanges(updateData)
    }

    const handleLikertLabelChange = (id, value) => {
        setCustLikertRangeError([]);
        const updateData = custLikertRanges.map(item => {
            if (item.id === id) {
                item.label = value
            }
            return item;
        })
        setCustLikertRanges(updateData)
    }

    const custLikertRowsData = custLikertRanges.map((item) => {
        return {
            pointsRange: item.pointsRange,
            label: item.label,
            id: item.id,
            order: item.order
        };
    });

    const custLikertColumns = [
        {
            field: 'likertRating',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "25px"}}>
                        Rating
                    </Typography>
                </Box>
            ),
            minWidth: 150,
            maxWidth: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <FormControl required>
                        <Controller
                            rules={{required: true}}
                            control={control}
                            defaultValue={params.row.order}
                            key={maxStars}
                            value={params.row.order}
                            name={`${params.row.id}likertRating`}
                            render={({
                                         name,
                                         value
                                     }) => (
                                <CustomizedRatings selected={value}
                                name={name}
                                readOnly={true}
                                max={maxStars}
                                />
                            )}
                        />
                    </FormControl>
                )
            }
        },
        {
            field: 'pointsRange',
            headerName: 'Range',
            renderHeader: (params) => (
                <Box>
                    <Typography size="lg" variant="label">
                        Points
                    </Typography>
                    <Typography size="md" variant="label" >
                        Range
                    </Typography>
                </Box>
            ),
            minWidth: 120,
            maxWidth: 120,
            sortable: false,
            renderCell: (params) => {
                return (
                    <FormControl required>
                        <Controller
                            rules={{required: true}}
                            control={control}
                            defaultValue={params.row.pointsRange}
                            value={params.row.pointsRange}
                            name={`${params.row.id}pointsRange`}
                            render={({
                                         onChange,
                                         value
                                     }) => (
                                <Input
                                    id={`${params.row.id}pointsRange`}
                                    value={value}
                                    onChange={(e) => {
                                        onChange(e.target.value)
                                        handleLikertPointsRangeChange(params.row.id, e.target.value);
                                    }}
                                    endAdornment={
                                        <InputAdornment position="end">%</InputAdornment>}
                                />
                            )}
                        />
                    </FormControl>
                )
            }
        },
        {
            field: 'label',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "25px"}}>
                        Description
                    </Typography>
                </Box>
            ),
            minWidth: 650,
            maxWidth: 650,
            sortable: false,
            renderCell: (params) => {
                return (
                    <FormControl fullWidth required>
                        <Controller
                            rules={{required: true}}
                            control={control}
                            defaultValue={params.row.label}
                            value={params.row.label}
                            name={`${params.row.id}label`}
                            render={({
                                         onChange,
                                         value
                                     }) => (
                                <Input fullWidth
                                    placeholder="Write a label"
                                    id={`${params.row.id}label`}
                                    value={value}
                                    inputProps={
                                       {maxLength: 750}
                                    }
                                    onChange={(e) => {
                                        onChange(e.target.value)
                                        handleLikertLabelChange(params.row.id, e.target.value);
                                    }}
                                />
                            )}
                        />
                    </FormControl>
                )
            }
        },
        {
            field: 'id',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "25px"}}>
                        Action
                    </Typography>
                </Box>
            ),
            sortable: false,
            renderCell: (params) => {
                if (params.row.order === 1 || params.row.order === custLikertRanges.length) {
                    return (
                        <></>
                    )
                } else {
                    return (
                        <IconButton aria-label="delete" edge="end"
                                    onClick={() => handleLikertRowDelete(params.row.id)}>
                            <DeleteIcon/>
                        </IconButton>
                    )
                }
            }
        }
    ];

    const processCriteriaData = (categories, data) => {
        let item = filterCategories(categories, data.category.id)
        let ratings = data.ratings.length ? data.ratings : state.ratings
        setSubCategories(item[0].subCategories);
        if (data.description)
            setDescCharacterCount(data.description.length)
        if (data.calculation)
            setCalcCharacterCount(data.calculation.length)
        setState({
            ...state,
            responseType: data.responseType.id.toString(),
            status: data.status,
            criteria: data.criteria,
            category: data.category.id.toString(),
            subCategory: data.subCategory ? data.subCategory.id.toString() : "",
            functionalAreas: data.functionalAreas,
            subFunctionalAreas: data.subFunctionalAreas,
            stakeholders: data.stakeholders,
            ratings: ratings,
            description: data.description,
            calculation: data.calculation,
            ranges: data.ranges,
            custLikertRanges: data.custLikertRanges
        });
        if (data.ranges && data.ranges.length > 0) {
            data.ranges.map((item) => {
                item.id = item.order;
                return item;
            });
            setIsShow(true)
            setRanges(data.ranges)
            setRangeCount(data.ranges.length)
        } else {
            setIsShow(false)
            setRanges(CREATE_CRITERIA_QUANTITATIVE_STATE)
        }

        if (data.custLikertRanges && data.custLikertRanges.length > 0) {
            data.custLikertRanges.map((item) => {
                item.id = item.order;
                return item;
            });
            setCustLikertRanges(data.custLikertRanges)
            setCustLikertRangeCount(data.custLikertRanges.length)
            setMaxStars(data.custLikertRanges.length)
        } else {
            setCustLikertRanges(CREATE_CRITERIA_CUSTOM_LIKERT_STATE)
            setMaxStars(2)
        }
    }

    const getCategories = async () => {
        try {
            return await authAxios.get(`/categories`);
        } catch (e) {
            console.error(e);
        }
    };
    const getFunctionalArea = async () => {
        try {
            return await authAxios.get(`/functional-areas`);
        } catch (e) {
            console.error(e);
        }
    };
    const getStakeholders = async (functionalAreaCodes) => {
        if (functionalAreaCodes != null && functionalAreaCodes.length > 0) {
            const codesAsString = functionalAreaCodes.map(functionalAreCode => functionalAreCode.description).toString();
            try {
                return await authAxios.get(`/stakeholders?functionalAreaCodes=` + codesAsString);
            } catch (e) {
                console.error(e);
            }
        }
        return [];
    };
    const getCriteriaDetails = async () => {
        try {
            return await authAxios.get(`/criteria/${criteriaId}`);
        } catch (e) {
            console.error(e.response.status);
            if (e.response.status === 404)
                setIs404Error(true)
            else
                enqueueToast({
                    variant: "error",
                    title: "System Error",
                    message: e.response ? e.response.data[e.response.status] : "",
                    isPersistent: true
                });
        }
    };

    useEffect(() => {
        getFunctionalArea().then(res => {
            setFunctionalAreas(res.data);
        });
        if (!criteriaId) {
            getCategories().then(res => {
                setCategories(res.data);
            });
            setIsLoading(false);
        } else {
            getCategories()
                .then(res => {
                    setCategories(res.data);
                    return res.data;
                }).then((categories) =>
                getCriteriaDetails().then(res => {
                    if (res) {
                        getStakeholders(res.data.functionalAreas).then(res => {
                            setStakeholders(res.data);
                        });
                        processCriteriaData(categories, res.data);
                        setIsLoading(false)
                    } else {
                        setIsLoading(false)
                    }
                })
            )
        }

        return () => {
            setCategories(null);
            resetState()
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState]);

    useEffect(() => {
        if (state.functionalAreas && state.functionalAreas.length > 0) {
            setIsFunctionalAreaError(false)
            setFunctionalAreaErrorDesc("")
        }
        if (functionalAreas.length > 0) {
            const subFunctionalAreaList = getSelectedSubFunctionalAreas(state.functionalAreas, functionalAreas);
            setSubFunctionalAreas([...new Set(subFunctionalAreaList.flat())])
        }
    }, [functionalAreas, state.functionalAreas]);

    useEffect(() => {
        if (state.ranges && state.ranges.length === 0) {
            setRanges(isShow ? CREATE_CRITERIA_QUANTITATIVE_STATE : [])
        }
    }, [isShow, state.ranges]);

    useEffect(() => {
        if (state.custLikertRanges && state.custLikertRanges.length === 0) {
            setCustLikertRanges(CREATE_CRITERIA_CUSTOM_LIKERT_STATE)
        }
    }, [state.custLikertRanges]);

    useEffect(() => {
        if (state.subFunctionalAreas && state.subFunctionalAreas.length > 0) {
            setIsSubFuncAreaError(false)
            setSubFuncAreaErrorDesc("")
        }
    }, [state.subFunctionalAreas]);

    return (
        <form onSubmit={handleSubmit(onSubmit, onError)}>
            <Grid container alignItems="center">
                {!is404Error && (
                    <>
                        <Grid item xs={8} className="sticky-header" style={{height: 60}}>
                            <Typography variant="h1" component="h1">
                                {name}
                            </Typography>
                        </Grid>
                        <Grid item xs={4} className="sticky-header" style={{height: 60}}>
                            <Box display="flex" justifyContent="flex-end">
                                {criteriaId ? (
                                    <>
                                        <ModalDialog
                                            titleText={"Deactivate Criteria"}
                                            saveButtonText={"Deactivate"}
                                            content={<React.Fragment>Are you sure you want to deactivate this
                                                criteria?<br/><br/>This criteria will no longer be available in future
                                                campaigns</React.Fragment>}
                                            onSave={onStatusUpdate}
                                            page={"Criteria"}
                                        />
                                        <Button variant="secondary" style={{marginRight: "1rem"}} size="large"
                                                onClick={() => {
                                                    onCancel()
                                                }}>Cancel</Button>
                                        <Button variant="primary" size="large" onClick={() => {
                                            onUpdate()
                                        }}>Save</Button>
                                    </>
                                ) : (
                                    <>
                                        <Button variant="secondary" size="large" style={{marginRight: "1rem"}}
                                                onClick={() => {
                                                    onCancel()
                                                }}>Cancel</Button>
                                        <Button variant="primary" size="large" type="submit">Save</Button>
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </>
                )}
                <Grid item xs={12}>

                    {isLoading ? (
                        <Loading/>
                    ) : (
                        <>
                            {is404Error ? (
                                <NotFoundPage backPage={"criteria-catalog"}/>
                            ) : (
                                <Box mr={4}>
                                    <Card>
                                        <CardContent>
                                            <Grid container alignItems="center" spacing={5}>
                                                <Grid item xs={5}>
                                                    <FormRadio
                                                        defState={state.responseType}
                                                        name={"responseType"}
                                                        options={respTypeOptions}
                                                        onTypeChange={onRespTypeChange}
                                                        control={control}
                                                        showLabel={true}
                                                    />
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <FormSelect
                                                        label={"category-label"}
                                                        defValue={state.category}
                                                        name={"category"}
                                                        options={categories}
                                                        onTypeChange={onCategorySelect}
                                                        control={control}
                                                        required={true}
                                                        isError={errors.category}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <FormSelect
                                                        label={"sub-category-label"}
                                                        defValue={state.subCategory}
                                                        name={"subCategory"}
                                                        options={subCategories}
                                                        onTypeChange={onSubCategorySelect}
                                                        control={control}
                                                        required={false}
                                                    />
                                                </Grid>
                                                <Grid item xs={5}>
                                                    <AutoCompleteCheckbox
                                                        name={"functionalAreas"}
                                                        label={"Functional Area"}
                                                        showLabel={true}
                                                        options={functionalAreas}
                                                        control={control}
                                                        defValue={state.functionalAreas}
                                                        onOptionsChange={handleFunctionalArea}
                                                        isError={isFunctionalAreaError}
                                                        errorDesc={functionalAreaErrorDesc}
                                                        required={true}
                                                    />
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <AutoCompleteCheckbox
                                                        name={"subFunctionalAreas"}
                                                        label={"Sub Functional Area"}
                                                        options={subFunctionalAreas}
                                                        control={control}
                                                        defValue={state.subFunctionalAreas}
                                                        onOptionsChange={handleSubFunctionalArea}
                                                        isError={isSubFuncAreaError}
                                                        errorDesc={subFuncAreaErrorDesc}
                                                        required={true}
                                                        showLabel={true}
                                                    />
                                                </Grid>
                                                {(state.responseType === "1" || state.responseType === "2" || state.responseType === "4") && (
                                                    <Grid item xs={12}>
                                                        <AutoCompleteCheckbox
                                                            name={"stakeholders"}
                                                            label={"Suggested Stakeholder Type"}
                                                            options={stakeholders}
                                                            control={control}
                                                            defValue={state.stakeholders}
                                                            onOptionsChange={handleStakeholder}
                                                            required={true}
                                                            showLabel={true}
                                                        />
                                                    </Grid>
                                                )}
                                                <Grid item xs={12}>
                                                    <FormControl required fullWidth>
                                                        <InputLabel htmlFor="criteria">
                                                            Criteria
                                                        </InputLabel>
                                                        {criteriaId ? (
                                                            <Controller
                                                                rules={{required: true}}
                                                                control={control}
                                                                defaultValue={state.criteria}
                                                                name="criteria"
                                                                render={({onChange, value}) => (
                                                                    <Input fullWidth placeholder="Write a Question"
                                                                           id="criteria"
                                                                           multiline={true}
                                                                           aria-multiline={true}
                                                                           value={state.criteria}
                                                                           minRows={2}
                                                                           rowsMin={2}
                                                                           onChange={(e) => {
                                                                               onChange(e);
                                                                               onCriteriaChange(e);
                                                                           }}/>
                                                                )}
                                                            />
                                                        ) : (
                                                            <Controller
                                                                rules={{required: true}}
                                                                control={control}
                                                                defaultValue=""
                                                                name="criteria"
                                                                as={
                                                                    <Input fullWidth placeholder="Write a Question"
                                                                           id="criteria"
                                                                           multiline={true}
                                                                           aria-multiline={true}
                                                                           minRows={2}
                                                                           rowsMin={2}/>
                                                                }
                                                            />
                                                        )}
                                                    </FormControl>
                                                </Grid>
                                                {state.responseType === "2" && (
                                                    <Grid item xs={12}>
                                                        <Box mb={-4}>
                                                            <Typography size="sm" fontWeight="semibold" component="p"
                                                                        display="block">
                                                                Rating Description
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                )}
                                                {state.responseType === "2" && likertScaleList.map((currentRating, index) => {
                                                    return (
                                                        <React.Fragment key={index}>
                                                            <Grid item xs={2}>
                                                                <CustomizedRatings
                                                                    selected={currentRating.selected}
                                                                    readOnly={true}/>
                                                            </Grid>
                                                            <Grid item xs={10}>
                                                                <FormControl fullWidth>
                                                                    {criteriaId ? (
                                                                        <Controller
                                                                            control={control}
                                                                            defaultValue={state.ratings[index] ? state.ratings[index].description : ""}
                                                                            name={`rating.${index}`}
                                                                            render={({onChange, value}) => (
                                                                                <Input fullWidth
                                                                                       placeholder={`"${currentRating.name}" description`}
                                                                                       id={`${index}`}
                                                                                       multiline={true}
                                                                                       aria-multiline={true}
                                                                                       minRows={2}
                                                                                       rowsMin={2}
                                                                                       inputProps={
                                                                                           {maxLength: 750}
                                                                                       }
                                                                                       value={state.ratings[index] ? state.ratings[index].description : ""}
                                                                                       onChange={(e) => {
                                                                                           onChange(e);
                                                                                           onRatingsChange(e)
                                                                                       }}/>
                                                                            )}
                                                                        />
                                                                    ) : (
                                                                        <Controller
                                                                            control={control}
                                                                            defaultValue=""
                                                                            name={`rating.${index}`}
                                                                            as={
                                                                                <Input fullWidth
                                                                                       placeholder={`"${currentRating.name}" description`}
                                                                                       id={`rating.${index}`}
                                                                                       multiline={true}
                                                                                       aria-multiline={true}
                                                                                       minRows={2} rowsMin={2}
                                                                                       inputProps={
                                                                                           {maxLength: 750}
                                                                                       }/>
                                                                            }
                                                                        />
                                                                    )}
                                                                </FormControl>
                                                            </Grid>
                                                        </React.Fragment>
                                                    );
                                                })}
                                                {(state.responseType === "3" || state.responseType === "4") && (
                                                    <>
                                                        <Grid item xs={12}>
                                                            <FormControl fullWidth>
                                                                <InputLabel htmlFor="description">
                                                                    Description
                                                                    <Box display="inline" ml={1}>
                                                                        <Typography size="xs" component="span"
                                                                                    color="tertiary">
                                                                            (Optional)
                                                                        </Typography>
                                                                    </Box>
                                                                </InputLabel>
                                                                <Controller
                                                                    control={control}
                                                                    defaultValue={state.description}
                                                                    name={"description"}
                                                                    render={({onChange, value}) => (
                                                                        <Input
                                                                            onChange={(e) => {
                                                                                setDescCharacterCount(e.target.value.length)
                                                                                onChange(e.target.value)
                                                                                onDescChange(e);
                                                                            }}
                                                                            value={value}
                                                                            multiline={true}
                                                                            aria-multiline={true}
                                                                            placeholder={"Write a Description"}
                                                                            minRows={2}
                                                                            rowsMin={2}
                                                                            inputProps={
                                                                                {maxLength: 750}
                                                                            }/>
                                                                    )}
                                                                />
                                                                <Box display="flex" justifyContent="flex-end">
                                                                    <Typography variant="caption" color="secondary"
                                                                                component="p">
                                                                        {descCharacterCount} / 750
                                                                    </Typography>
                                                                </Box>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <FormControl fullWidth>
                                                                <InputLabel htmlFor="calculation">
                                                                    Calculation
                                                                    <Box display="inline" ml={1}>
                                                                        <Typography size="xs" component="span"
                                                                                    color="tertiary">
                                                                            (Optional)
                                                                        </Typography>
                                                                    </Box>
                                                                </InputLabel>
                                                                <Controller
                                                                    control={control}
                                                                    defaultValue={state.calculation}
                                                                    name={"calculation"}
                                                                    render={({onChange, value}) => (
                                                                        <Input
                                                                            onChange={(e) => {
                                                                                setCalcCharacterCount(e.target.value.length)
                                                                                onChange(e.target.value)
                                                                                onCalcDescChange(e)
                                                                            }}
                                                                            value={value}
                                                                            multiline={true}
                                                                            aria-multiline={true}
                                                                            placeholder={"Write a Calculation Description"}
                                                                            minRows={2}
                                                                            rowsMin={2}
                                                                            inputProps={
                                                                                {maxLength: 750}
                                                                            }/>
                                                                    )}
                                                                />
                                                                <Box display="flex" justifyContent="flex-end">
                                                                    <Typography variant="caption" color="secondary"
                                                                                component="p">
                                                                        {calcCharacterCount} / 750
                                                                    </Typography>
                                                                </Box>
                                                            </FormControl>
                                                        </Grid>
                                                        {state.responseType === "3" && (
                                                            <>
                                                        {isShow && (
                                                            <Grid item xs={12}>
                                                                <DataGrid
                                                                    className={classes.root}
                                                                    style={{backgroundColor: "white"}}
                                                                    rows={rowsData}
                                                                    columns={columns}
                                                                    hideFooter
                                                                    checkboxSelection={false}
                                                                    disableSelectionOnClick
                                                                    autoHeight={true}
                                                                />
                                                            </Grid>
                                                        )}

                                                        {rangeError.length > 0 && rangeError.map((rangeErrorObj, index) => {
                                                            return (
                                                                <React.Fragment key={index}>
                                                                    <Grid item xs={12}>
                                                                        <Box display="flex" justifyContent="flex-start">
                                                                            <Typography color="error" size="xs">
                                                                                {rangeErrorObj}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                        <Grid item xs={12}>
                                                            <Box display="flex" justifyContent="flex-start" mt={2}>
                                                                {isShow && (
                                                                    <>
                                                                        <Button variant="primary"
                                                                                onClick={handleRangeAdd}
                                                                                startIcon={<AddCircleOutlineIcon/>}> Add
                                                                            Row</Button>
                                                                        <ModalDialog
                                                                            titleText={"Delete Range"}
                                                                            content={<React.Fragment>Are you sure you
                                                                                want to delete the entire
                                                                                range?</React.Fragment>}
                                                                            saveButtonText={"Delete"}
                                                                            onSave={handleRangeHide}
                                                                            page={"Criteria"}
                                                                            type={"range"}
                                                                        />
                                                                    </>
                                                                )}
                                                                {!isShow && (
                                                                    <Button variant="primary" onClick={handleRangeShow}
                                                                            startIcon={<AddCircleOutlineIcon/>}> Add
                                                                        Range</Button>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                    </>
                                                )}
                                                {state.responseType === "4" && (
                                                    <>
                                                        <Grid item xs={12}>
                                                        <DataGrid
                                                            className={classes.root}
                                                            style={{backgroundColor: "white"}}
                                                            rows={custLikertRowsData}
                                                            columns={custLikertColumns}
                                                            hideFooter
                                                            checkboxSelection={false}
                                                            disableSelectionOnClick
                                                            autoHeight={true}
                                                        />
                                                        </Grid>
                                                        {custLikertRangeError.length > 0 && custLikertRangeError.map((custLikertRangeErrorObj, index) => {
                                                            return (
                                                                <React.Fragment key={index}>
                                                                    <Grid item xs={12}>
                                                                        <Box display="flex" justifyContent="flex-start">
                                                                            <Typography color="error" size="xs">
                                                                                {custLikertRangeErrorObj}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                        <>
                                                            <Grid item xs={12}>
                                                                <Box display="flex" justifyContent="flex-start" mt={2}>
                                                                     <Button variant="primary"
                                                                                    onClick={handleLikertRangeAdd}
                                                                                    startIcon={<AddCircleOutlineIcon/>}> Add
                                                                                Row</Button>
                                                                </Box>
                                                            </Grid>
                                                        </>
                                                    </>
                                                )}
                                                </>
                                            )}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Box>
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
        </form>
    );
}
